'use client'

import { ArrowLeft, ArrowRight, ArrowUpRight } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { useCallback, useEffect, useState } from 'react'

import { LocaleLink } from '@/components/LocaleLink'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Carousel, CarouselContent, CarouselItem, type CarouselApi } from '@/components/ui/carousel'
import { cn } from '@/utilities/ui'

import { HomeCardImage } from './HomeCardImage'
import { CategoryLabel, ReadTime, SectionHeading, type SectionHeadingLevel } from './common'
import {
  HOME_BI_NEWS_IMAGE_HOVER,
  HOME_BI_NEWS_ITEM_GROUP,
  HOME_BI_NEWS_TITLE_LINK,
  HOME_BI_SECTION_HEADING,
  HOME_BI_X_CAROUSEL_CONTAINER,
  HOME_BI_X_CAROUSEL_SHELL,
  HOME_BI_X_CAROUSEL_TRACK,
} from './home-bi-layout'
import type { DestaquePublicacao } from './types'

/**
 * FAIXA "CARROSSEL DE NOTÍCIA" — imagem em cima, título abaixo, descrição abaixo do título.
 *
 * ── POR QUE ESTE COMPONENTE EXISTE ──────────────────────────────────────────────────────────────
 * A faixa «Posts no X» (`HomeXPostsBand`) tem o carrossel certo, mas o card errado para este uso:
 * ele é o cartão de tweet (avatar, @handle, texto solto) e **não tem categoria**. Esta faixa usa o
 * item editorial (`DestaquePublicacao`) — o mesmo do «home-meio» —, então ganha categoria, resumo
 * e tempo de leitura de graça.
 *
 * 🔴 POR QUE A CASCA DO CARROSSEL É DUPLICADA E NÃO EXTRAÍDA (decisão de 02/09/2026).
 * O caminho «limpo» seria extrair a casca do `HomeXPostsBand` e partilhá-la. **Não foi feito de
 * propósito:** o dono pediu explicitamente que a faixa do X não regredisse, e a extração exigiria
 * reescrever justamente esse arquivo, que está no ar e funcionando. Aqui a duplicação de ~40 linhas
 * de estrutura compra isolamento total — nenhuma linha do bloco existente é tocada. Se um dia as
 * duas faixas precisarem mudar juntas, aí sim vale extrair, com as duas cobertas por teste.
 */

/** Mesma régua da faixa do X: 1 card no telemóvel, 2 no tablet, 4 no desktop. */
const CARD_BASIS =
  'basis-[calc((100cqw-0px)/1.08)] sm:basis-[calc((100cqw-1rem)/2)] lg:basis-[calc((100cqw-3rem)/4)]'

function HomeNewsCard({
  item,
  showThumbnail,
}: {
  item: DestaquePublicacao
  showThumbnail: boolean
}) {
  return (
    // 🔴 `HOME_BI_NEWS_ITEM_GROUP` é literalmente `group`, e é a ÂNCORA das variantes
    // `group-hover:` — sem ela, o zoom da imagem e o sublinhado do título ficam INERTES (as
    // regras nunca casam). Mesmo par que `HomeBiNewsStoryRow` e as demais faixas usam.
    <Card
      className={cn(
        HOME_BI_NEWS_ITEM_GROUP,
        'flex h-full min-w-0 flex-col gap-3 rounded-xl border bg-card p-4 shadow-none ring-1 ring-foreground/10',
      )}
    >
      {showThumbnail && item.img ? (
        <div className="relative aspect-16/10 w-full overflow-hidden rounded-(--post-thumb-radius,0px)">
          <HomeCardImage
            src={item.img}
            srcSizes={item.imgSizes}
            alt={item.alt}
            className={HOME_BI_NEWS_IMAGE_HOVER}
            sizes="(min-width: 1024px) 25vw, (min-width: 640px) 50vw, 92vw"
          />
        </div>
      ) : null}

      {item.category ? (
        <div className="flex min-w-0 items-center gap-2">
          <CategoryLabel href={item.categoryHref} className="min-w-0 truncate">
            {item.category}
          </CategoryLabel>
        </div>
      ) : null}

      <h3 className="min-w-0 text-base leading-snug font-semibold">
        <LocaleLink href={item.href} prefetch={false} className={HOME_BI_NEWS_TITLE_LINK}>
          {item.title}
        </LocaleLink>
      </h3>

      {item.description ? (
        <p className="text-muted-foreground line-clamp-3 text-sm leading-relaxed">
          {item.description}
        </p>
      ) : null}

      {item.read ? (
        <div className="mt-auto pt-1">
          <ReadTime>{item.read}</ReadTime>
        </div>
      ) : null}
    </Card>
  )
}

export type HomeNewsCarouselBandProps = {
  items: DestaquePublicacao[]
  sectionHeading?: string
  sectionHeadingLevel?: SectionHeadingLevel
  showSectionHeader?: boolean
  showMoreLink?: boolean
  moreTopicLabel?: string
  moreHref?: string
  headingId?: string
  showThumbnail?: boolean
  /** Barra de pílulas (tags), injetada por `HomeBandFilterable` logo abaixo do título. */
  filterSlot?: React.ReactNode
}

export function HomeNewsCarouselBand({
  items,
  sectionHeading,
  sectionHeadingLevel = 'h2',
  showSectionHeader = true,
  showMoreLink = false,
  moreTopicLabel,
  moreHref,
  headingId = 'home-news-carousel-heading',
  showThumbnail = true,
  filterSlot,
}: HomeNewsCarouselBandProps) {
  const t = useTranslations('Home')
  const [api, setApi] = useState<CarouselApi>()
  const [canPrev, setCanPrev] = useState(false)
  const [canNext, setCanNext] = useState(false)
  const [snaps, setSnaps] = useState<number[]>([])
  const [selected, setSelected] = useState(0)

  // Mesmo padrão do carrossel da faixa do X: TUDO dentro de `sync`, e `reInit` recebe a MESMA
  // referência — senão o `off` do cleanup não a remove e o ouvinte vaza a cada re-render.
  useEffect(() => {
    if (!api) return
    const sync = () => {
      setCanPrev(api.canScrollPrev())
      setCanNext(api.canScrollNext())
      setSnaps(api.scrollSnapList())
      setSelected(api.selectedScrollSnap())
    }
    sync()
    api.on('select', sync)
    api.on('reInit', sync)
    return () => {
      api.off('select', sync)
      api.off('reInit', sync)
    }
  }, [api])

  const scrollTo = useCallback((i: number) => api?.scrollTo(i), [api])

  if (items.length === 0) return null

  return (
    // Sem cabeçalho não há elemento com esse id: apontar para ele deixaria a secção com um nome
    // acessível quebrado. Mesma guarda das faixas irmãs.
    <section
      data-home-section="home-news-carousel"
      aria-labelledby={showSectionHeader ? headingId : undefined}
      className="min-w-0"
    >
      {showSectionHeader ? (
        <header className="section-heading mb-3 flex items-center justify-between gap-4 md:mb-4">
          {/* 🔴 `globals.css` zera `font-size`/`font-weight` de h1–h6 no @layer base, então um
              `<h2>` sem este token sai do TAMANHO DO CORPO. Todas as faixas aplicam-no. */}
          <SectionHeading
            id={headingId}
            level={sectionHeadingLevel}
            className={cn('heading min-w-0 pr-3', HOME_BI_SECTION_HEADING)}
          >
            {sectionHeading}
          </SectionHeading>
          {showMoreLink && moreHref ? (
            <LocaleLink
              href={moreHref}
              data-component-type="cta-more-link-arrow"
              className="cta-more-link-arrow group inline-flex h-8 min-h-8 shrink-0 items-center gap-2 rounded-sm py-0 focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
            >
              <span className="link-text text-xs leading-none font-semibold tracking-wide whitespace-nowrap">
                {t('viewMore')}
                {moreTopicLabel ? (
                  <span className="link-text-label">&nbsp;{moreTopicLabel}</span>
                ) : null}
              </span>
              <span
                className="icon-wrapper bg-foreground text-background flex size-6 shrink-0 items-center justify-center rounded-full transition-opacity group-hover:opacity-90"
                aria-hidden
              >
                <ArrowUpRight className="size-3" strokeWidth={2.25} />
              </span>
            </LocaleLink>
          ) : null}
        </header>
      ) : null}

      {/* As pílulas ficam ENTRE o título e os cards — é o pedido do dono. */}
      {filterSlot ? <div className="mb-4">{filterSlot}</div> : null}

      <div className={HOME_BI_X_CAROUSEL_SHELL}>
        <div className={HOME_BI_X_CAROUSEL_CONTAINER}>
          <Carousel
            setApi={setApi}
            opts={{ align: 'start', containScroll: 'trimSnaps' }}
            className={HOME_BI_X_CAROUSEL_TRACK}
          >
            <CarouselContent className="-ml-4">
              {items.map((item, i) => (
                <CarouselItem key={`${item.href}-${i}`} className={cn('min-w-0 pl-4', CARD_BASIS)}>
                  <HomeNewsCard item={item} showThumbnail={showThumbnail} />
                </CarouselItem>
              ))}
            </CarouselContent>
          </Carousel>
          <div
            aria-hidden
            className="pointer-events-none absolute inset-y-0 right-0 z-10 w-(--home-x-peek) bg-linear-to-l from-muted/40 to-transparent"
          />
        </div>
      </div>

      {snaps.length > 1 ? (
        <div className="mt-4 flex items-center justify-between gap-4 lg:mt-5">
          <div className="flex items-center" role="tablist" aria-label={t('postsNav')}>
            {snaps.map((_, i) => (
              <button
                key={i}
                type="button"
                role="tab"
                aria-selected={i === selected}
                aria-label={t('goToGroup', { current: i + 1, total: snaps.length })}
                onClick={() => scrollTo(i)}
                // Área de toque de 24px (target-size); a bolinha visível continua com 8px.
                className="group flex h-6 min-w-6 items-center justify-center focus-visible:outline-none"
              >
                <span
                  aria-hidden
                  className={cn(
                    'h-2 rounded-full transition-all group-focus-visible:ring-2 group-focus-visible:ring-ring',
                    i === selected ? 'w-8 bg-foreground' : 'w-2 bg-muted-foreground/35',
                  )}
                />
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="size-8 rounded-full"
              aria-label={t('prevPosts')}
              disabled={!canPrev}
              onClick={() => api?.scrollPrev()}
            >
              <ArrowLeft className="size-4" />
            </Button>
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="size-8 rounded-full"
              aria-label={t('nextPosts')}
              disabled={!canNext}
              onClick={() => api?.scrollNext()}
            >
              <ArrowRight className="size-4" />
            </Button>
          </div>
        </div>
      ) : null}
    </section>
  )
}
